// Payment Settings - Stripe Connect onboarding and payment configuration
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEventTypes } from '../../services/scheduling-api';
import { EventType } from '../../types/scheduling';
import './SchedulingStyles.css';

// Stripe Connect status types
type ConnectStatus = 'not_connected' | 'pending' | 'connected';

interface StripeConnectStatus {
  status: ConnectStatus;
  accountId?: string;
  payoutsEnabled?: boolean;
  chargesEnabled?: boolean;
  detailsSubmitted?: boolean;
}

interface PaymentSettingsProps {
  plan: 'free' | 'pro';
}

const API_BASE = '/backend/scheduling';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const PaymentSettings: React.FC<PaymentSettingsProps> = ({ plan }) => {
  const [loading, setLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState<StripeConnectStatus>({ status: 'not_connected' });
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [connectResponse, eventTypesResponse] = await Promise.all([
        fetch(`${API_BASE}/payments/connect/status`, {
          method: 'GET',
          headers: getAuthHeaders()
        }),
        getEventTypes()
      ]);

      if (connectResponse.ok) {
        const status = await connectResponse.json();
        setConnectStatus(status);
      }

      setEventTypes(eventTypesResponse.eventTypes);
    } catch (err) {
      console.error('Error loading payment settings:', err);
      setError('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectWithStripe = async () => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/payments/connect/onboard`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start onboarding');
      }

      const { onboardingUrl } = await response.json();
      window.location.href = onboardingUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect with Stripe');
      setActionLoading(false);
    }
  };

  const handleViewDashboard = async () => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/payments/connect/dashboard`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get dashboard URL');
      }

      const { dashboardUrl } = await response.json();
      window.open(dashboardUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open dashboard');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (cents: number, currency: string): string => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="scheduling-loading">
        <p>Loading payment settings...</p>
      </div>
    );
  }

  const isProUser = plan === 'pro';
  const canAcceptPayments = isProUser && connectStatus.status === 'connected';
  const paidEventTypes = eventTypes.filter(et => et.paymentRequired);

  return (
    <div className="scheduling-container">
      <div className="scheduling-header">
        <h2>Payment Settings</h2>
        <p>Accept payments for your bookings with Stripe</p>
      </div>

      {error && (
        <div className="scheduling-alert scheduling-alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}

      {/* Plan Status Section */}
      {!isProUser && (
        <div className="scheduling-card" style={{ backgroundColor: '#fefce8', borderColor: '#fef08a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>&#128274;</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#854d0e' }}>
                Join the Supporters Club to Accept Payments
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#a16207' }}>
                Payment collection is included in the Supporters Club ($10/year)
              </p>
            </div>
            <Link
              to="/account"
              className="scheduling-btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Join Supporters Club
            </Link>
          </div>
        </div>
      )}

      {/* Stripe Connect Status Section */}
      <div className="scheduling-card">
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Stripe Connect</h3>

        {connectStatus.status === 'not_connected' && (
          <div>
            <p style={{ margin: '0 0 16px', color: '#64748b', lineHeight: '1.6' }}>
              Connect your Stripe account to receive payments directly from clients.
              Stripe handles all payment processing securely.
            </p>
            <button
              onClick={handleConnectWithStripe}
              disabled={!isProUser || actionLoading}
              className="scheduling-btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: !isProUser ? 0.5 : 1,
                cursor: !isProUser ? 'not-allowed' : 'pointer'
              }}
            >
              <span style={{ fontSize: '18px' }}>&#128179;</span>
              {actionLoading ? 'Connecting...' : 'Connect with Stripe'}
            </button>
            {!isProUser && (
              <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                Join the Supporters Club to enable payment collection
              </p>
            )}
          </div>
        )}

        {connectStatus.status === 'pending' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '24px' }}>&#9888;</span>
              <div>
                <p style={{ margin: 0, fontWeight: '500', color: '#92400e' }}>
                  Setup Incomplete
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#a16207' }}>
                  Please complete your Stripe account setup to start accepting payments
                </p>
              </div>
            </div>
            <button
              onClick={handleConnectWithStripe}
              disabled={actionLoading}
              className="scheduling-btn-primary"
            >
              {actionLoading ? 'Loading...' : 'Complete Setup'}
            </button>
          </div>
        )}

        {connectStatus.status === 'connected' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#dcfce7',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '24px', color: '#16a34a' }}>&#10003;</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '500', color: '#166534' }}>
                  Stripe Account Connected
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#15803d' }}>
                  You can now accept payments for your bookings
                </p>
              </div>
              <button
                onClick={handleViewDashboard}
                disabled={actionLoading}
                className="scheduling-btn-secondary"
              >
                View Stripe Dashboard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Event Types with Payments */}
      {isProUser && connectStatus.status === 'connected' && (
        <div className="scheduling-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>Paid Event Types</h3>
            <Link
              to="/scheduling/event-types"
              style={{ fontSize: '14px', color: '#3b82f6' }}
            >
              Manage Event Types
            </Link>
          </div>

          {paidEventTypes.length === 0 ? (
            <div className="scheduling-empty">
              <p style={{ margin: 0 }}>No paid event types configured</p>
              <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
                Enable payments in your event type settings to require payment before booking
              </p>
            </div>
          ) : (
            <div className="scheduling-list">
              {paidEventTypes.map(et => (
                <div
                  key={et.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${et.color || '#3b82f6'}`
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontWeight: '500' }}>{et.title}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                      {et.durationMinutes} minutes
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '18px', color: '#059669' }}>
                      {formatCurrency(et.paymentAmountCents || 0, et.paymentCurrency)}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                      {et.paymentCurrency.toUpperCase()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p style={{ margin: '16px 0 0', fontSize: '13px', color: '#64748b' }}>
            To enable payments for an event type, edit the event type and toggle "Require payment"
          </p>
        </div>
      )}

      {/* Platform Fee Info */}
      <div className="scheduling-card">
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Fee Structure</h3>

        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '500' }}>Platform Fee</span>
              <span style={{ fontWeight: '600', color: '#059669' }}>0.5%</span>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>
              Larry takes a small platform fee on each transaction
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '500' }}>Stripe Processing</span>
              <span style={{ fontWeight: '600', color: '#64748b' }}>2.9% + $0.30</span>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>
              Standard Stripe payment processing fees
            </p>
          </div>
        </div>

        {/* Example Calculation */}
        <div style={{
          marginTop: '24px',
          padding: '20px',
          backgroundColor: '#eff6ff',
          borderRadius: '8px',
          border: '1px solid #bfdbfe'
        }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#1e40af' }}>
            Example: $150 Therapy Session
          </h4>
          <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>Client pays</span>
              <span style={{ fontWeight: '500' }}>$150.00</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>Stripe fee (2.9% + $0.30)</span>
              <span style={{ color: '#dc2626' }}>-$4.65</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>Platform fee (0.5%)</span>
              <span style={{ color: '#dc2626' }}>-$0.75</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '8px',
              borderTop: '1px solid #bfdbfe',
              fontWeight: '600'
            }}>
              <span style={{ color: '#1e40af' }}>You receive</span>
              <span style={{ color: '#059669' }}>$144.60</span>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="scheduling-card">
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>How It Works</h3>

        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              flexShrink: 0
            }}>
              1
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: '500' }}>Connect your Stripe account</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                Stripe handles all payment processing and compliance
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              flexShrink: 0
            }}>
              2
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: '500' }}>Enable payments on event types</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                Set prices for each event type you want to charge for
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              flexShrink: 0
            }}>
              3
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: '500' }}>Clients pay when booking</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                Payment is collected via Stripe checkout before confirming the booking
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              flexShrink: 0
            }}>
              4
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: '500' }}>Get paid automatically</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#64748b' }}>
                Funds are deposited to your bank account via Stripe (daily or instant payouts)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Policy Note */}
      {canAcceptPayments && (
        <div className="scheduling-card">
          <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Refund Policy</h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
            You control your own refund policy. When a client cancels, you can issue a refund
            through your Stripe dashboard. Refund fees are the responsibility of the account holder.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
